import { track } from '@/lib/analytics';
import type { StorefrontHomeCard } from '../api/types';

/**
 * Catálogo de eventos da vitrine.
 *
 * Convenção herdada do catálogo público (`features/catalog`):
 * `substantivo_verbo_no_passado`, snake_case. O prefixo `storefront_` separa a
 * vitrine do catálogo de kits, que já ocupa `catalog_*` e `kit_*` no mesmo
 * projeto do PostHog.
 *
 * ## O que NUNCA entra num payload daqui
 *
 * A regra é a mesma do `FORBIDDEN_FIELDS` do backend, e vale por construção:
 * nenhum helper deste módulo aceita esses campos, e nenhum call site os monta.
 *
 * - **Custo e formação de preço:** `purchase_price`, `price_factor`,
 *   `supplier_code`, `nf_barcode`, `ncm`. Preço de VENDA e etiqueta podem ir —
 *   são o que a tela já mostra ao cliente.
 * - **Profundidade de estoque:** `available_for_offer`, `max_addable`,
 *   `available_quantity`. O que viaja é o booleano derivado (`can_add`,
 *   `sold_out`, `partial`), que responde "dá para comprar?" sem revelar
 *   quantas unidades existem.
 * - **PII:** e-mail, nome, telefone, CNPJ. Nenhum PAYLOAD de evento carrega
 *   dado pessoal. A identificação existe, mas mora fora daqui: o `AuthProvider`
 *   chama `identifyUser` (`lib/analytics.ts`) com o `_id` opaco do usuário e o
 *   `role`, nada além. Não chame `identify` de dentro da vitrine — havendo dois
 *   lugares que identificam, um deles vai divergir do outro.
 *
 * `results_total` e `found` contam MODELOS que casam a busca — é largura de
 * catálogo, não profundidade de estoque, e é o mesmo número que a toolbar
 * anuncia em voz alta na tela.
 */
export const STOREFRONT_EVENT = {
  // ---- Visualização (uma vez por identidade, nunca por render) ----
  homeViewed: 'storefront_home_viewed',
  catalogViewed: 'storefront_catalog_viewed',
  productViewed: 'storefront_product_viewed',
  cartViewed: 'storefront_cart_viewed',
  notFoundViewed: 'storefront_not_found_viewed',
  /** Estado de indisponibilidade renderizado no lugar do conteúdo. */
  unavailableShown: 'storefront_unavailable_shown',

  // ---- Cliques e seleções ----
  productCardClicked: 'storefront_product_card_clicked',
  brandCardClicked: 'storefront_brand_card_clicked',
  sectionLinkClicked: 'storefront_section_link_clicked',
  categorySelected: 'storefront_category_selected',
  sizeSelected: 'storefront_size_selected',
  /** Menu do botão flutuante aberto — métrica da flag `help-button-label`. */
  helpMenuOpened: 'storefront_help_menu_opened',
  helpOptionSelected: 'storefront_help_option_selected',
  /** Mensagem do diálogo aceita pelo servidor — o resultado do teste A/B. */
  helpRequestSent: 'storefront_help_request_sent',

  // ---- Pronta entrega (hero da home e modal de boas-vindas) ----
  /** Hero ≥ 50% visível, uma vez por montagem. `photos` é quantas fotos havia. */
  readyDeliveryBannerViewed: 'storefront_ready_delivery_banner_viewed',
  /** `target: 'cta' | 'photo'`; foto leva `product_id` e `position`. */
  readyDeliveryBannerClicked: 'storefront_ready_delivery_banner_clicked',
  readyDeliveryModalViewed: 'storefront_ready_delivery_modal_viewed',
  readyDeliveryModalCtaClicked: 'storefront_ready_delivery_modal_cta_clicked',
  /** `method: 'later' | 'close' | 'escape' | 'outside'`. */
  readyDeliveryModalDismissed: 'storefront_ready_delivery_modal_dismissed',

  // ---- Busca, filtro e paginação ----
  filterApplied: 'storefront_filter_applied',
  filtersCleared: 'storefront_filters_cleared',
  sortChanged: 'storefront_sort_changed',
  pageChanged: 'storefront_page_changed',
  searchSubmitted: 'storefront_search_submitted',
  searchSuggestionSelected: 'storefront_search_suggestion_selected',

  // ---- Carrinho e fechamento ----
  addedToCart: 'storefront_product_added_to_cart',
  cartQuantityChanged: 'storefront_cart_quantity_changed',
  removedFromCart: 'storefront_product_removed_from_cart',
  checkoutStarted: 'storefront_checkout_started',
  checkoutCompleted: 'storefront_checkout_completed',
  checkoutFailed: 'storefront_checkout_failed',

  // ---- Saída e abandono (critérios em `hooks/use-exit-tracking.ts`) ----
  exited: 'storefront_exited',
  cartAbandoned: 'storefront_cart_abandoned',
} as const;

/**
 * Emite um evento da vitrine sem poder derrubar a tela.
 *
 * `track()` já é no-op sem `VITE_POSTHOG_KEY`, mas com a key presente ele chama
 * `posthog.capture`, que é código de terceiro rodando dentro de um `onClick`.
 * Uma exceção ali sobe pelo handler e leva junto a navegação que o clique ia
 * fazer — a instrumentação passaria a decidir se o produto abre. O `catch`
 * existe para isso, e é silencioso de propósito: um `console.error` por clique
 * numa falha persistente polui o console do cliente sem lhe dar nada.
 */
export function trackStorefront(
  event: string,
  props?: Record<string, unknown>
): void {
  try {
    track(event, props);
  } catch {
    // Analytics nunca é motivo para a vitrine parar de funcionar.
    return;
  }
}

/**
 * O que um card da home leva num evento de clique.
 *
 * Centralizado porque são TRÊS componentes desenhando o mesmo card
 * (`HighlightCard`, `HomeProductCard` e, por tabela, a faixa curada) — três
 * payloads montados à mão divergiriam no primeiro campo novo.
 *
 * `sold_out` e `sizes_count` são os derivados que a tela já mostra: o primeiro
 * é o selo "Esgotado", o segundo é quantos chips de tamanho aparecem. Nenhum
 * dos dois revela quantas unidades existem.
 */
export function homeCardProps(card: StorefrontHomeCard) {
  return {
    product_id: card.product_id,
    model_key: card.model_key,
    brand: card.brand,
    sale_price: card.sale_price,
    retail_price: card.retail_price,
    sold_out: card.sold_out,
    sizes_count: card.sizes.length,
  };
}
